import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import KodaCareLogoFull from '../../src/components/Custom Icons/KodaCareLogoFull';
import { useAuth } from '../../src/context/AuthContext';
import { websitePath } from '../lib/routes';

const APP_HOST = import.meta.env.VITE_APP_HOST || 'https://app.kodacare.pet';

export function PetSittersNavbar() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (mobileOpen && navRef.current && !navRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [mobileOpen]);

  const scrollToForm = () => {
    setMobileOpen(false);
    window.dispatchEvent(new Event('kc-sitter-apply-code'));
  };

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <button onClick={() => navigate(websitePath('/petsitters'))} className="text-primary-brown" aria-label="KodaCare Sitters home">
          <KodaCareLogoFull style={{ width: 180, height: 32 }} height={32} />
        </button>

        {/* Centre: eyebrow + Pet Parents nav link */}
        <div className="hidden md:flex items-center gap-4">
          <p className="text-[11px] font-black tracking-[0.22em] uppercase text-primary-brown">For professionals</p>
          <span className="text-gray-300 select-none">|</span>
          <button
            onClick={() => navigate(websitePath('/'))}
            className="text-xs font-bold tracking-widest text-gray-700 hover:text-primary-brown transition-colors uppercase"
          >
            For Pet Parents
          </button>
        </div>

        {/* Right: Login (unauthenticated only) + CTA */}
        <div className="hidden md:flex items-center gap-3">
          {!isAuthenticated && (
            <button
              onClick={() => { window.location.href = `${APP_HOST}/login`; }}
              className="text-sm font-medium text-gray-700 hover:text-primary-brown transition-colors px-2"
            >
              Login
            </button>
          )}

          <button
            onClick={scrollToForm}
            className="rounded-full bg-primary-brown px-5 py-2 text-sm font-bold text-white hover:bg-primary-brown/80 transition-colors"
          >
            Activate Free Sitter Account
          </button>
        </div>

        <button
          className="md:hidden w-10 h-10 grid place-items-center text-gray-700"
          onClick={() => setMobileOpen(v => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-2">
          <p className="text-[11px] font-black tracking-[0.22em] uppercase text-primary-brown py-1">For professionals</p>
          <button
            onClick={() => {
              navigate(websitePath('/'));
              setMobileOpen(false);
            }}
            className="text-left text-sm font-bold tracking-widest text-gray-700 hover:text-primary-brown py-3 border-b border-gray-50 uppercase"
          >
            For Pet Parents
          </button>
          {!isAuthenticated && (
            <button
              onClick={() => { window.location.href = `${APP_HOST}/login`; }}
              className="text-left text-sm font-bold text-primary-brown py-2"
            >
              Login
            </button>
          )}
          <button
            onClick={scrollToForm}
            className="mt-2 rounded-xl bg-primary-brown px-5 py-3 text-sm font-bold text-white hover:bg-primary-brown/80 transition-colors"
          >
            Activate Free Sitter Account
          </button>
        </div>
      )}
    </nav>
  );
}
